import { Building2, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { paths } from "@/config/paths";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AvatarMenu from "./avatar-menu";
import { usePortalUnreadCount, usePortalMarkNotificationRead, usePortalMarkAllNotificationsRead } from "@/features/portal/api/portal-api";
import NotificationDropdown from "@/features/notifications/components/notification-dropdown";

type SafeProject = {
  id: string;
  name: string;
};

type PortalTopBarProps = {
  projects: SafeProject[];
  activeProjectId?: string;
  activeProjectName?: string;
};

const PortalTopBar = ({
  projects,
  activeProjectId,
  activeProjectName,
}: PortalTopBarProps) => {
  const navigate = useNavigate();

  const { data: unreadCount = 0 } = usePortalUnreadCount();
  const markRead = usePortalMarkNotificationRead();
  const markAllRead = usePortalMarkAllNotificationsRead();

  const renderProjectSwitch = () => {
    if (projects.length <= 1) {
      return (
        <span className="text-sm font-medium text-(--color-text-primary) truncate">
          {activeProjectName ?? "—"}
        </span>
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-1.5 text-sm font-medium text-(--color-text-primary) hover:text-(--color-text-secondary) transition-colors"
            aria-label="Switch project"
          >
            <span className="truncate max-w-[40vw]">
              {activeProjectName ?? "Select project"}
            </span>
            <ChevronDown className="w-4 h-4 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="min-w-56">
          {projects.map((project) => (
            <DropdownMenuItem
              key={project.id}
              onClick={() =>
                navigate(paths.app.portal.project.getHref(project.id))
              }
            >
              {project.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <header className="h-14 bg-(--color-bg-surface) border-b border-(--color-border-default) flex items-center justify-between px-4 md:px-6 shrink-0 z-10 gap-3">
      {/* ── Logo (left) ── */}
      <button
        onClick={() =>
          navigate(
            activeProjectId
              ? paths.app.portal.project.getHref(activeProjectId)
              : paths.app.portal.getHref()
          )
        }
        className="flex items-center gap-2 shrink-0"
        aria-label="Home"
      >
        <div className="w-8 h-8 rounded-lg bg-(--color-warning-500) flex items-center justify-center">
          <Building2 className="w-4.5 h-4.5 text-white" />
        </div>
        <span className="hidden sm:block text-base font-semibold text-(--color-text-primary)">
          Villa
        </span>
      </button>

      {/* ── Project switcher (center) ── */}
      <div className="flex-1 flex justify-center min-w-0">
        {renderProjectSwitch()}
      </div>

      {/* ── Actions (right) ── */}
      <div className="flex items-center gap-1.5 shrink-0">
        <NotificationDropdown
          unreadCount={unreadCount}
          onMarkRead={(id) => markRead.mutate(id)}
          onMarkAllRead={() => markAllRead.mutate(undefined)}
          viewAllHref={paths.app.notifications.getHref()}
          isPortal
        />

        <AvatarMenu />
      </div>
    </header>
  );
};

export default PortalTopBar;
